import React, { useState, useEffect } from 'react';
import { connectionsAPI } from '../../api/connections';
import toast from 'react-hot-toast';

const Connections = () => {
  const [activeConnections, setActiveConnections] = useState([]);
  const [historyConnections, setHistoryConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('active'); // 'active' или 'history'
  const [filters, setFilters] = useState({
    proxyId: '',
    status: ''
  });

  useEffect(() => {
    loadConnections();
    // Обновляем данные каждые 10 секунд
    const interval = setInterval(() => {
      if (tab === 'active') {
        loadActiveConnections();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [tab, filters]);

  const loadConnections = () => {
    if (tab === 'active') {
      loadActiveConnections();
    } else {
      loadHistoryConnections();
    }
  };

  const loadActiveConnections = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.proxyId) params.proxyId = filters.proxyId;
      
      const data = await connectionsAPI.getActive(params);
      setActiveConnections(data.data || []);
    } catch (error) {
      console.error('Error loading active connections:', error);
      toast.error('Ошибка загрузки активных подключений');
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryConnections = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.proxyId) params.proxyId = filters.proxyId;
      if (filters.status) params.status = filters.status;
      
      const data = await connectionsAPI.getHistory(params);
      setHistoryConnections(data.data || []);
    } catch (error) {
      console.error('Error loading connection history:', error);
      toast.error('Ошибка загрузки истории подключений');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0с';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м ${secs}с`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      active: 'Активно',
      closed: 'Завершено',
      error: 'Ошибка'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getProtocolBadge = (protocol) => {
    const color = protocol === 'tcp' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${color}`}>
        {protocol?.toUpperCase() || 'TCP'}
      </span>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Активные подключения</h1>
        <p className="text-gray-600">Отслеживайте свои активные подключения к прокси-серверам</p>
      </div>

      {/* Табы */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setTab('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              tab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Активные</span>
              {activeConnections.length > 0 && (
                <span className="bg-blue-100 text-blue-800 py-0.5 px-2 rounded-full text-xs">
                  {activeConnections.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              tab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>История</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Фильтры */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID прокси
            </label>
            <input
              type="text"
              value={filters.proxyId}
              onChange={(e) => setFilters({ ...filters, proxyId: e.target.value })}
              placeholder="Все прокси"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {tab === 'history' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Статус
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все статусы</option>
                <option value="active">Активные</option>
                <option value="closed">Завершённые</option>
                <option value="error">С ошибкой</option>
              </select>
            </div>
          )}
          <div className="flex items-end">
            <button
              onClick={loadConnections}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>
        </div>
      </div>

      {/* Таблица подключений */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="animate-spin h-8 w-8 mx-auto mb-4 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Загрузка данных...
          </div>
        ) : (tab === 'active' ? activeConnections : historyConnections).length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-lg font-medium">Нет подключений</p>
            <p className="text-sm mt-2">
              {tab === 'active' 
                ? 'У вас пока нет активных подключений к прокси-серверам'
                : 'История подключений пуста'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Прокси
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Порт
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Протокол
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Клиент
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Начало
                  </th>
                  {tab === 'history' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Конец
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Длительность
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Трафик
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(tab === 'active' ? activeConnections : historyConnections).map((connection) => (
                  <tr key={connection.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {connection.proxy?.name || connection.proxyId}
                          </div>
                          {connection.proxy?.countryName && (
                            <div className="text-xs text-gray-500">
                              {connection.proxy.countryName} ({connection.proxy.countryCode})
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {connection.serverPort}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getProtocolBadge(connection.protocol)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {connection.clientIp || '-'}
                      </div>
                      {connection.clientPort && (
                        <div className="text-xs text-gray-500">
                          :{connection.clientPort}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(connection.startTime)}
                    </td>
                    {tab === 'history' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(connection.endTime)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDuration(connection.duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatBytes(connection.totalBytes)}
                      </div>
                      <div className="text-xs text-gray-500">
                        ↑{formatBytes(connection.bytesSent)} ↓{formatBytes(connection.bytesReceived)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(connection.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Информация */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Информация:</span> Данные об активных подключениях обновляются автоматически каждые 10 секунд. 
              Трафик рассчитывается на основе данных от прокси-сервера через heartbeat.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connections;
